/* [EZ] Brand Ambassador — progressive enhancement for the page's two interactive bands.

   1. Expandable cards ("A Passionate Landscape"). The card body is clamped in CSS to the
      authored collapsed height. This script measures each one and, ONLY where the content
      actually overflows, adds .is-clamped (which applies the fade) and reveals the Read More
      button at the bottom of the card. A card whose content already fits shows no link.

      When the list carries [data-ez-exp-accordion] (the "One card open at a time" setting),
      opening a card collapses whichever sibling was open — one open at a time.

   2. Product row carousel (phone only). The row itself is the scroll-snap track; each
      .ez-pcard is a cell. Builds pagination dots, keeps the current dot in sync while
      swiping, and scrolls on dot click.

   No core edits; re-runs on shopify:section:load so the Customizer stays accurate. */
(function () {
  var MOBILE = '(max-width: 760px)';

  /* ---------- 1 · expandable cards ------------------------------------- */

  function collapsedHeight(item) {
    // The collapsed max-height lives in CSS (breakpoint-dependent); read it back
    // rather than duplicating the breakpoint logic here.
    var body = item.querySelector('[data-ez-exp-body]');
    if (!body) return 0;
    return body.clientHeight;
  }

  function measure(item) {
    var body = item.querySelector('[data-ez-exp-body]');
    var more = item.querySelector('[data-ez-exp-more]');
    if (!body || !more) return;

    // Never measure while open — scrollHeight would equal clientHeight.
    var wasOpen = item.classList.contains('is-open');
    if (wasOpen) return;

    var overflows = body.scrollHeight > collapsedHeight(item) + 2;

    item.classList.toggle('is-clamped', overflows);
    more.hidden = !overflows;
  }

  function close(item) {
    var body = item.querySelector('[data-ez-exp-body]');
    var btn = item.querySelector('[data-ez-exp-btn]');
    if (!body || !item.classList.contains('is-open')) return;
    // Pin the live height first so the collapse animates from a real number.
    item.style.setProperty('--ba-exp-full', body.scrollHeight + 'px');
    void body.offsetHeight;
    item.classList.remove('is-open');
    item.style.removeProperty('--ba-exp-full');
    if (btn) {
      btn.setAttribute('aria-expanded', 'false');
      btn.textContent = btn.dataset.more;
    }
  }

  function toggle(item) {
    var body = item.querySelector('[data-ez-exp-body]');
    var btn = item.querySelector('[data-ez-exp-btn]');
    if (!body || !btn) return;

    var open = !item.classList.contains('is-open');

    // Accordion: opening one collapses whichever sibling was open.
    var list = item.closest('[data-ez-exp-accordion]');
    if (open && list) {
      Array.prototype.forEach.call(list.querySelectorAll('[data-ez-exp]'), function (other) {
        if (other !== item) close(other);
      });
    }

    if (open) {
      // Give the transition a concrete target, then release it once settled.
      item.style.setProperty('--ba-exp-full', body.scrollHeight + 'px');
      item.classList.add('is-open');
    } else {
      // Pin the current height so the collapse animates from a real number.
      item.style.setProperty('--ba-exp-full', body.scrollHeight + 'px');
      // force a frame so the browser registers the pinned height
      void body.offsetHeight;
      item.classList.remove('is-open');
      item.style.removeProperty('--ba-exp-full');
    }

    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    btn.textContent = open ? btn.dataset.less : btn.dataset.more;
  }

  function initExpandables(root) {
    var items = root.querySelectorAll('[data-ez-exp]');
    Array.prototype.forEach.call(items, function (item) {
      measure(item);

      var btn = item.querySelector('[data-ez-exp-btn]');
      if (!btn || btn.dataset.ezBound) return;
      btn.dataset.ezBound = '1';
      btn.addEventListener('click', function () {
        toggle(item);
      });
    });
  }

  /* ---------- 2 · product carousel ------------------------------------- */

  function initCarousel(section) {
    var track = section.querySelector('[data-ez-ba-track]');
    var dots = section.querySelector('[data-ez-ba-dots]');
    if (!track || !dots) return;

    var cells = track.querySelectorAll('.ez-pcard');
    if (cells.length < 2) {
      dots.hidden = true;
      return;
    }

    if (!window.matchMedia(MOBILE).matches) {
      dots.hidden = true;
      return;
    }

    // (Re)build the dots to match the current cell count.
    dots.innerHTML = '';
    for (var i = 0; i < cells.length; i++) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'ez-ba-products__dot';
      dot.setAttribute('aria-label', 'Go to product ' + (i + 1));
      dot.setAttribute('aria-current', i === 0 ? 'true' : 'false');
      dot.dataset.index = String(i);
      dots.appendChild(dot);
    }
    dots.hidden = false;

    if (!dots.dataset.ezBound) {
      dots.dataset.ezBound = '1';
      dots.addEventListener('click', function (e) {
        var dot = e.target.closest('.ez-ba-products__dot');
        if (!dot) return;
        var cell = track.querySelectorAll('.ez-pcard')[+dot.dataset.index];
        if (cell) track.scrollTo({ left: cell.offsetLeft - track.offsetLeft, behavior: 'smooth' });
      });
    }

    if (!track.dataset.ezBound) {
      track.dataset.ezBound = '1';
      var raf = null;
      track.addEventListener(
        'scroll',
        function () {
          if (raf) return;
          raf = window.requestAnimationFrame(function () {
            raf = null;
            var all = track.querySelectorAll('.ez-pcard');
            var mid = track.scrollLeft + track.clientWidth / 2;
            var active = 0;
            var best = Infinity;
            for (var i = 0; i < all.length; i++) {
              var c = all[i];
              var centre = c.offsetLeft - track.offsetLeft + c.offsetWidth / 2;
              var d = Math.abs(centre - mid);
              if (d < best) {
                best = d;
                active = i;
              }
            }
            var buttons = dots.querySelectorAll('.ez-ba-products__dot');
            for (var j = 0; j < buttons.length; j++) {
              buttons[j].setAttribute('aria-current', j === active ? 'true' : 'false');
            }
          });
        },
        { passive: true }
      );
    }
  }

  /* ---------- boot ------------------------------------------------------ */

  function init(root) {
    var scope = root || document;
    initExpandables(scope);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-ez-ba-products]'), initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Fonts landing late change the measured height — re-measure once they are ready.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () {
      init(document);
    });
  }

  var resizeTimer = null;
  window.addEventListener('resize', function () {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () {
      init(document);
    }, 150);
  });

  // Theme editor: re-run whenever a section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
  document.addEventListener('shopify:block:select', function (e) {
    init(e.target.closest('.ez-ba') || document);
  });
})();
